
import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Search } from 'lucide-react';

interface Breeder {
  id: string;
  first_name: string;
  last_name: string;
  business_name: string | null;
  profile_image_url: string | null;
}

interface AddTopBreederDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddTopBreederDialog = ({ open, onOpenChange, onSuccess }: AddTopBreederDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBreeder, setSelectedBreeder] = useState<Breeder | null>(null);
  const { toast } = useToast();

  const { data: breeders, isLoading } = useQuery({
    queryKey: ['available-breeders', searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('profiles')
        .select('id, first_name, last_name, business_name, profile_image_url')
        .eq('user_type', 'breeder');

      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,business_name.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query.limit(10);
      if (error) throw error;
      return data as Breeder[];
    },
    enabled: open,
  });

  const addTopBreeder = useMutation({
    mutationFn: async () => {
      if (!selectedBreeder) throw new Error('No breeder selected');

      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError || !user.user) throw new Error('User not authenticated');

      // Get the current max display order and add 1
      const { data: maxOrderData } = await supabase
        .from('top_breeders')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      const nextOrder = maxOrderData && maxOrderData.length > 0 
        ? maxOrderData[0].display_order + 1 
        : 1;

      const { error } = await supabase
        .from('top_breeders')
        .insert({
          breeder_id: selectedBreeder.id,
          selected_by: user.user.id,
          display_order: nextOrder,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Top breeder added successfully.",
      });
      onSuccess();
      onOpenChange(false);
      setSelectedBreeder(null);
      setSearchTerm('');
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to add top breeder. They may already be selected.",
        variant: "destructive",
      });
    },
  });

  const getInitials = (breeder: Breeder) => {
    return breeder.first_name.charAt(0).toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Top Breeder</DialogTitle>
          <DialogDescription>
            Search and select a breeder to feature on the homepage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="search">Search Breeders</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                placeholder="Search by name or business..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {searchTerm && (
            <div className="max-h-48 overflow-y-auto border rounded-md">
              {isLoading ? (
                <p className="p-4 text-center text-sm text-gray-500">Searching...</p>
              ) : breeders?.length === 0 ? (
                <p className="p-4 text-center text-sm text-gray-500">No breeders found.</p>
              ) : (
                breeders?.map((breeder) => (
                  <div
                    key={breeder.id}
                    className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                      selectedBreeder?.id === breeder.id ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => setSelectedBreeder(breeder)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center">
                        {breeder.profile_image_url ? (
                          <img
                            src={breeder.profile_image_url}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-400 via-green-500 to-green-600 flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                              {getInitials(breeder)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {breeder.first_name} {breeder.last_name}
                        </p>
                        {breeder.business_name && (
                          <p className="text-xs text-gray-600">{breeder.business_name}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {selectedBreeder && (
            <div className="p-3 border rounded-md bg-blue-50">
              <p className="font-medium text-sm">Selected:</p>
              <div className="flex items-center space-x-3 mt-2">
                <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center">
                  {selectedBreeder.profile_image_url ? (
                    <img
                      src={selectedBreeder.profile_image_url}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-green-400 via-green-500 to-green-600 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        {getInitials(selectedBreeder)}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-sm">
                  {selectedBreeder.first_name} {selectedBreeder.last_name}
                  {selectedBreeder.business_name && ` - ${selectedBreeder.business_name}`}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => addTopBreeder.mutate()}
              disabled={!selectedBreeder || addTopBreeder.isPending}
            >
              Add Top Breeder
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
